import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import classNames from 'classnames';
import { getGlobalStyleSheets } from '../../global-styles';
import { enumAttributeConverter } from '../enum-types';
import { AtlSpinnerProps, SpinnerSize, SpinnerAlignment } from './props';

import * as TagNames from './spinner.tagnames';

@customElement(TagNames.InlineLoader)
export class AtlInlineLoader extends LitElement implements AtlSpinnerProps {
   declare internalId: string;
   declare internalClassName?: string;
   declare size: SpinnerSize;
   declare align?: SpinnerAlignment;

   static properties = {
      internalId: { type: String },
      internalClassName: { type: String },
      size: {
         type: String,
         converter: enumAttributeConverter(SpinnerSize, SpinnerSize.small),
      },
      align: {
         type: String,
         converter: enumAttributeConverter(
            SpinnerAlignment,
            SpinnerAlignment.TopCenter
         ),
      },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .loader {
            width: 60px;
            aspect-ratio: 4;
            --_g: no-repeat
               radial-gradient(
                  circle closest-side,
                  rgba(var(--bs-primary-rgb), var(--bs-text-opacity)) 90%,
                  #0000
               );
            background:
               var(--_g) 0% 50%,
               var(--_g) 50% 50%,
               var(--_g) 100% 50%;
            background-size: calc(100% / 3) 100%;
            animation: l7 1s infinite linear;
         }
         @keyframes l7 {
            33% {
               background-size:
                  calc(100% / 3) 0%,
                  calc(100% / 3) 100%,
                  calc(100% / 3) 100%;
            }
            50% {
               background-size:
                  calc(100% / 3) 100%,
                  calc(100% / 3) 0%,
                  calc(100% / 3) 100%;
            }
            66% {
               background-size:
                  calc(100% / 3) 100%,
                  calc(100% / 3) 100%,
                  calc(100% / 3) 0%;
            }
         }
      `,
   ];

   getSpinnerSize = () => {
      switch (this.size) {
         case SpinnerSize.large:
            return 'width: 4rem; height: 4rem;';
         case SpinnerSize.medium:
            return 'width: 3rem; height: 3rem;';
         case SpinnerSize.small:
            return 'width: 2rem; height: 2rem;';
         default:
            return 'width: 2rem; height: 2rem;';
      }
   };

   spinnerAlignmentClass = () => {
      switch (this.align) {
         case SpinnerAlignment.TopCenter:
            return 'align-items-start justify-content-center';
         case SpinnerAlignment.Middle:
            return 'align-items-center justify-content-center';
         default:
            return 'align-items-center justify-content-center';
      }
   };

   spinner = () => {
      return html`
         <div
            id=${this.internalId}
            class=${classNames(
               'loader',
               `text-primary`,
               this.internalClassName
            )}
            style=${this.getSpinnerSize()}
         >
            <span class="visually-hidden">Loading...</span>
         </div>
      `;
   };

   render() {
      return html`
         <div
            class=${classNames(
               'w-100 h-100 d-flex',
               this.spinnerAlignmentClass()
            )}
         >
            ${this.spinner()}
         </div>
      `;
   }
}
